import Ajv from "ajv";
import { CP_INITIATED, OcppProtocol, SEND_MESSAGES } from "./protocol";

export type JsonSchema = Record<string, any>;
export type MessageKind = "req" | "conf";

/** An OCPP-J error (CALLERROR) raised while validating or handling a message. */
export class OcppError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly details: Record<string, unknown> = {}
  ) {
    super(message);
    this.name = "OcppError";
  }
}

// ajv keyword -> OCPP-J error code (OCPP-J 1.6 section 4.2.3 / OCPP 2.x part 4 section 4.3).
const ERROR_CODES: Record<string, string> = {
  maximum: "FormatViolation",
  minimum: "FormatViolation",
  maxLength: "FormatViolation",
  minLength: "FormatViolation",
  format: "FormatViolation",
  exclusiveMaximum: "OccurrenceConstraintViolation",
  exclusiveMinimum: "OccurrenceConstraintViolation",
  multipleOf: "OccurrenceConstraintViolation",
  maxItems: "OccurrenceConstraintViolation",
  minItems: "OccurrenceConstraintViolation",
  maxProperties: "OccurrenceConstraintViolation",
  minProperties: "OccurrenceConstraintViolation",
  required: "OccurrenceConstraintViolation",
  additionalItems: "OccurrenceConstraintViolation",
  pattern: "PropertyConstraintViolation",
  propertyNames: "PropertyConstraintViolation",
  additionalProperties: "PropertyConstraintViolation",
  enum: "PropertyConstraintViolation",
  type: "TypeConstraintViolation",
};

export interface ProtocolCatalog {
  protocol: OcppProtocol;
  actions: string[];
  cpInitiated: ReadonlySet<string>;
  csmsInitiated: ReadonlySet<string>;
  sendMessages: ReadonlySet<string>;
  schema(action: string, kind: MessageKind): JsonSchema | undefined;
  /** Throws OcppError with the matching OCPP-J error code when `payload` is invalid. */
  validate(action: string, kind: MessageKind, payload: unknown): void;
}

const parseSchemaId = (id: string): { action: string; kind: MessageKind } | null => {
  const legacy = /^urn:(.+)\.(req|conf)$/.exec(id);
  if (legacy) return { action: legacy[1], kind: legacy[2] as MessageKind };
  const modern = /^urn:(.+)(Request|Response)$/.exec(id);
  if (modern) return { action: modern[1], kind: modern[2] === "Request" ? "req" : "conf" };
  const send = /^urn:(\w+)$/.exec(id);
  if (send) return { action: send[1], kind: "req" };
  return null;
};

export const buildCatalog = (protocol: OcppProtocol, bundle: JsonSchema[]): ProtocolCatalog => {
  const ajv = new Ajv({ allErrors: false, multipleOfPrecision: 6 });
  const schemas = new Map<string, JsonSchema>();
  for (const schema of bundle) {
    const parsed = parseSchemaId(schema.$id);
    if (!parsed) throw new Error(`Unrecognised OCPP schema id '${schema.$id}' in ${protocol}`);
    const id = `urn:${parsed.action}.${parsed.kind}`;
    // ajv 6 cannot resolve the draft-07 meta-schema URL these files declare; drop it.
    const { $schema, ...rest } = schema;
    const rekeyed = { ...rest, $id: id };
    schemas.set(id, rekeyed);
    ajv.addSchema(rekeyed, id);
  }

  const actions = [...new Set([...schemas.keys()].map(id => parseSchemaId(id)!.action))].sort();
  const cpInitiated = new Set(CP_INITIATED[protocol]);
  const csmsInitiated = new Set(actions.filter(action => !cpInitiated.has(action) || action === "DataTransfer"));

  return {
    protocol,
    actions,
    cpInitiated,
    csmsInitiated,
    sendMessages: new Set(SEND_MESSAGES[protocol]),
    schema: (action, kind) => schemas.get(`urn:${action}.${kind}`),
    validate: (action, kind, payload) => {
      const id = `urn:${action}.${kind}`;
      const validator = ajv.getSchema(id);
      if (!validator) throw new OcppError("NotImplemented", `${action} is not an ${protocol} message`);
      if (!validator(payload)) {
        const [first] = validator.errors ?? [];
        throw new OcppError(
          ERROR_CODES[first?.keyword ?? ""] ?? "FormatViolation",
          `${action}.${kind}: ${ajv.errorsText(validator.errors)}`,
          { errors: validator.errors ?? [] }
        );
      }
    },
  };
};

// Schemas are large (2.1 alone is ~1 MB), so each version is loaded only when it is used.
const loaders: Record<OcppProtocol, () => Promise<JsonSchema[]>> = {
  "ocpp1.6": () => import("./schemas/ocpp1_6.json").then(m => (m as any).default ?? m),
  "ocpp2.0.1": () => import("./schemas/ocpp2_0_1.json").then(m => (m as any).default ?? m),
  "ocpp2.1": () => import("./schemas/ocpp2_1.json").then(m => (m as any).default ?? m),
};

const cache = new Map<OcppProtocol, Promise<ProtocolCatalog>>();

export const loadCatalog = (protocol: OcppProtocol): Promise<ProtocolCatalog> => {
  if (!cache.has(protocol)) {
    cache.set(
      protocol,
      loaders[protocol]().then(bundle => buildCatalog(protocol, bundle))
    );
  }
  return cache.get(protocol)!;
};
